import { X, Check, Clock, Heart, AlertOctagon, ShieldCheck } from "lucide-react";
import { SectionNumber } from "../visuals/SectionNumber";

const without = [
    { t: "00:00", title: "Emergency identified", desc: "Bystander realizes someone needs help.", icon: AlertOctagon },
    { t: "01:30", title: "Search for supplies", desc: "No OTC Naloxone visible on-site. Frantic calls, unclear location.", icon: Clock },
    { t: "03:00", title: "Uncertainty grows", desc: "Emergency supplies are somewhere — but nobody knows exactly where.", icon: AlertOctagon },
    { t: "07:00+", title: "Outcome", desc: "Help arrives after avoidable delays.", icon: X },
];

const withKairos = [
    { t: "00:00", title: "Emergency identified", desc: "Bystander realizes someone needs help.", icon: AlertOctagon },
    { t: "00:10", title: "Open the Kairos unit", desc: "Clearly labeled, one-touch release. No app, no account.", icon: ShieldCheck },
    { t: "00:15", title: "OTC Naloxone in hand", desc: "Staff + emergency services alerted automatically.", icon: Check },
    { t: "Within minutes", title: "Outcome", desc: "Emergency supplies delivered without delay.", icon: Heart },
];

export const Comparison = () => {
    return (
        <section
            id="comparison"
            data-testid="comparison-section"
            className="relative bg-white py-28 md:py-36 border-y border-slate-200"
        >
            <SectionNumber n={4} total={12} />
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal max-w-3xl">
                    <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Why Access Matters
                    </span>
                    <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                        The same moment.
                        <br />
                        Two very different
                        <br />
                        levels of readiness.
                    </h2>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Column
                        label="Without Kairos"
                        accent="#94A3B8"
                        bg="bg-slate-50"
                        items={without}
                        outcome="Delayed access. Uncertain response."
                        outcomeColor="text-slate-600"
                        testid="comparison-without"
                        muted
                    />
                    <Column
                        label="With Kairos"
                        accent="#F0493E"
                        bg="bg-[#0A1428]"
                        items={withKairos}
                        outcome="Instant access. Coordinated response. Auditable."
                        outcomeColor="text-white"
                        testid="comparison-with"
                        dark
                    />
                </div>
            </div>
        </section>
    );
};

const labelColorClass = (dark, muted) => {
    if (dark) return "text-white";
    if (muted) return "text-slate-500";
    return "text-slate-700";
};

const timeColorClass = (dark, muted) => {
    if (dark) return "text-[#F0493E]";
    if (muted) return "text-slate-400";
    return "text-slate-500";
};

const descColorClass = (dark, muted) => {
    if (dark) return "text-slate-400";
    if (muted) return "text-slate-500";
    return "text-slate-600";
};

const Column = ({
    label,
    accent,
    bg,
    items,
    outcome,
    outcomeColor,
    testid,
    dark = false,
    muted = false,
}) => (
    <div
        data-testid={testid}
        className={`${bg} border ${
            dark ? "border-white/10" : "border-slate-200"
        } p-8 md:p-10 flex flex-col`}
    >
        <div className="flex items-center gap-3">
            <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: accent }}
            />
            <span
                className={`text-[11px] font-bold uppercase tracking-[0.28em] ${labelColorClass(
                    dark,
                    muted
                )}`}
            >
                {label}
            </span>
        </div>

        <div className="mt-8 space-y-6 flex-1">
            {items.map((it) => (
                <div key={`${it.t}-${it.title}`} className="flex items-start gap-5">
                    <div
                        className={`shrink-0 w-16 font-display font-bold text-sm tnum tracking-tight ${timeColorClass(
                            dark,
                            muted
                        )}`}
                    >
                        {it.t}
                    </div>
                    <div className="flex-1 pb-6 border-b last:border-b-0 last:pb-0" style={{ borderColor: dark ? "rgba(255,255,255,0.08)" : "#E2E8F0" }}>
                        <div className="flex items-center gap-2">
                            <it.icon
                                size={16}
                                strokeWidth={1.5}
                                style={{ color: accent }}
                            />
                            <h4
                                className={`font-display font-bold text-base md:text-lg ${
                                    dark ? "text-white" : "text-[#0A1428]"
                                }`}
                            >
                                {it.title}
                            </h4>
                        </div>
                        <p
                            className={`mt-2 text-sm md:text-[15px] leading-relaxed font-medium ${descColorClass(
                                dark,
                                muted
                            )}`}
                        >
                            {it.desc}
                        </p>
                    </div>
                </div>
            ))}
        </div>

        <div
            className={`mt-8 pt-6 border-t ${
                dark ? "border-white/10" : "border-slate-200"
            }`}
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                Outcome
            </div>
            <div
                className={`mt-2 font-display font-extrabold text-2xl md:text-3xl tracking-tight ${outcomeColor}`}
            >
                {outcome}
            </div>
        </div>
    </div>
);
